import { useRequest } from "ahooks";
import dayjs from "dayjs";

import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";

import { getAlertSearchHistory } from "@/api/alert";
import usePlatform from "@/hooks/usePlatform";

interface AlertSearchHistoryListProps {
  open: boolean;
  onClose: () => void;
  alertId: string;
}
const AlertSearchHistoryList = (props: AlertSearchHistoryListProps) => {
  const { alertId, ...restProps } = props;

  const { isMobile } = usePlatform();

  const { loading, data: { data = [] } = {} } = useRequest(
    () => getAlertSearchHistory({ alert_id: alertId }),
    {
      ready: !!alertId && restProps.open,
    }
  );

  const formattedDate = (timestamp: string) => {
    return dayjs(parseInt(timestamp) * 1000).format("YYYY/MM/DD h A");
  };

  return (
    <Dialog {...restProps} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        Alert Search History
        <IconButton
          aria-label="close"
          onClick={() => restProps?.onClose?.()}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <GridCloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ height: "100%", borderCollapse: "collapse" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Search Time</TableCell>
                  <TableCell align="center">Matching Routes</TableCell>
                  <TableCell align="center">New Routes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <Stack spacing={1} alignItems="center">
                          <Typography variant="body2" color="text.secondary" align="center">
                            Looks like there's nothing here right now.
                          </Typography>
                          <Typography variant="body2" color="text.secondary" align="center">
                          This page will show your alert search history as soon as it's available—we want to keep things transparent.
                          </Typography>
                        </Stack>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  data
                    .sort((a, b) => parseInt(b.timestamp) - parseInt(a.timestamp))
                    .map((item) => (
                      <TableRow key={item.alert_id}>
                        <TableCell align="center">{formattedDate(item.timestamp)}</TableCell>
                        <TableCell align="center">{item.matching_routes_count}</TableCell>
                        <TableCell align="center">{item.new_routes_count}</TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};
export default AlertSearchHistoryList;
